import '../../styles/web-components/global-banner.scss';
import { BehaviorSubject, fromEvent, interval, Observable, tap, map, Subject, takeUntil } from 'rxjs';
import { preventAnimation, accessibilityEnabledListener, waitForTabbableElements } from '../services/accessibility.service';
import { headerTransition$, updateBodyPaddingTop, getElementOrParentByClassName } from '../helpers/utils';
import { isMobile } from '../vue/constants/devices';
import { createFocusTrap } from 'focus-trap';
const LEFT = -1;
const RIGHT = 1;
const CLICK_BUFFER = 3; // buffer in pixels to prevent a click from being interpreted as a swipe
class GlobalBanner extends HTMLElement {
    constructor() {
        super();
        this.selectedBannerId$ = new BehaviorSubject(undefined);
        this.stopRotation$ = new Subject();
        this.destroy$ = new Subject();
        this.swipeListenersAdded = false;
        this.updateBannersFunc = () => {
            console.debug("DY:updateBanners");
            this.reset();
        };
        this.addEventListener("click", (e) => {
            const target = e.target;
            const codeButtonEl = getElementOrParentByClassName(target, 'js-global-banner-code-button');
            if (codeButtonEl) {
                this.copyCode(codeButtonEl);
                return;
            }
            const detailsToggleEl = getElementOrParentByClassName(target, 'js-global-banner-detail-toggle');
            if (detailsToggleEl) {
                this.toggleDetails(detailsToggleEl);
                return;
            }
            const detailsCloseEl = getElementOrParentByClassName(target, 'js-global-banner-detail-close');
            if (detailsCloseEl) {
                const detailsToggleEl = detailsCloseEl.closest(".global-banner__banner").querySelector(".js-global-banner-detail-toggle");
                this.toggleDetails(detailsToggleEl, true);
                return;
            }
        });
    }
    connectedCallback() {
        var _a;
        this.device = this.getAttribute("display");
        const config = window.UTK_themeConfig && window.UTK_themeConfig[`${this.device}GlobalBanners`];
        this.container = this.querySelector('.global-banner');
        window.addEventListener("DY:updateBanners", this.updateBannersFunc);
        headerTransition$.subscribe({
            next: (smallerHeader) => {
                this.display(smallerHeader);
            },
            complete: () => {
                this.display(false);
            }
        });
        if ((_a = config === null || config === void 0 ? void 0 : config.banners) === null || _a === void 0 ? void 0 : _a.length) {
            this.setup();
        }
    }
    disconnectedCallback() {
        window.removeEventListener("DY:updateBanners", this.updateBannersFunc);
    }
    setup() {
        this.banners = Array.from(this.container.querySelectorAll(".js-global-banner:not(.js-global-banner-dynamic)"));
        this.arrows = Array.from(this.container.querySelectorAll(".js-global-banner-arrow-button"));
        this.detailToggles = Array.from(this.container.querySelectorAll(".js-global-banner-detail-toggle"));
        if (this.banners.length && !this.container.classList.contains('global-banner--has-initial-banners')) {
            // add initial banner class if dynamic banners added
            this.container.classList.add('global-banner--has-initial-banners');
        }
        if (this.banners.length > 1) {
            console.log(`GlobalBanner: setting up for multiple global banners for ${this.device}`);
            this.selectedBannerId$.next(this.banners[0].dataset.id);
            this.selectedBannerId$.pipe(takeUntil(this.destroy$), tap((id) => {
                this.banners.forEach((b) => {
                    if (b.dataset.id === id) {
                        b.classList.add("global-banner__banner--selected");
                    }
                    else {
                        b.classList.remove("global-banner__banner--selected");
                    }
                });
                this.detailToggles.forEach((el) => this.toggleDetails(el, true));
            })).subscribe();
            fromEvent(this.arrows, "click").pipe(takeUntil(this.destroy$), map(e => e.target), tap(el => this.selectedBannerId$.next(this.getNextBannerId(el.dataset.direction === "prev" ? LEFT : RIGHT))), tap(() => this.resetRotation())).subscribe();
            this.startRotation();
            // accessibility concerns
            fromEvent(this.container, "mouseenter").pipe(takeUntil(this.destroy$), tap(() => this.stopRotation())).subscribe();
            fromEvent(this.container, "mouseleave").pipe(takeUntil(this.destroy$), tap(() => this.startRotation())).subscribe();
            accessibilityEnabledListener().pipe(takeUntil(this.destroy$), tap(() => this.stopRotation())).subscribe();
            if (this.device === "mobile" && !this.swipeListenersAdded) {
                this.addSwipeListeners(this.container);
            }
            this.container.classList.remove("global-banner--hide-controls");
        }
        else if (this.banners.length === 1) {
            console.log(`GlobalBanner: setting up for one global banner for ${this.device}`);
            this.banners[0].classList.add("global-banner__banner--selected");
            this.rotationSub$ = new Observable().pipe(takeUntil(this.destroy$)).subscribe(); // dummy observable to prevent starting rotation (todo: better solution)
        }
    }
    reset() {
        this.debug("refreshing setup");
        this.destroy$.next();
        this.setup();
    }
    getNextBannerId(direction) {
        const selectedIdx = this.banners.findIndex((b) => b.classList.contains("global-banner__banner--selected"));
        const nextIdx = selectedIdx + (direction * 1);
        const newIdx = nextIdx === (this.banners.length) ? 0 : nextIdx === -1 ? this.banners.length - 1 : nextIdx;
        const nextBanner = this.banners[newIdx];
        return nextBanner.dataset.id;
    }
    stopRotation() {
        this.debug("stopping rotation");
        this.stopRotation$.next();
    }
    startRotation() {
        const rotationSpeed = +this.getAttribute("rotation");
        if (!preventAnimation() && (!this.rotationSub$ || this.rotationSub$.closed)) {
            this.debug("starting rotation");
            let obs;
            if (rotationSpeed) {
                const rotationMillis = rotationSpeed * 1000;
                obs = interval(rotationMillis).pipe(takeUntil(this.destroy$), takeUntil(this.stopRotation$), map(() => this.getNextBannerId(RIGHT)), tap(id => this.selectedBannerId$.next(id)));
            }
            else {
                obs = new Observable().pipe(takeUntil(this.destroy$), takeUntil(this.stopRotation$)); // dummy observable;
            }
            this.rotationSub$ = obs.subscribe();
        }
    }
    resetRotation() {
        this.debug("resetting rotation");
        this.stopRotation();
        this.startRotation();
    }
    addSwipeListeners(el) {
        this.debug("adding swipe listeners");
        let touchstartX = 0;
        let touchendX = 0;
        el.addEventListener('touchstart', e => {
            this.debug("touchstart");
            if (this.isBanner(e.target)) {
                touchstartX = e.changedTouches[0].screenX;
            }
        }, { passive: true });
        el.addEventListener('touchend', e => {
            this.debug("touchend");
            if (this.isBanner(e.target)) {
                touchendX = e.changedTouches[0].screenX;
                const diff = Math.abs(touchstartX - touchendX);
                if (diff < CLICK_BUFFER) {
                    // click
                    return;
                }
                let direction = RIGHT;
                if (touchendX > touchstartX) {
                    direction = LEFT;
                }
                const newBannerId = this.getNextBannerId(direction);
                this.selectedBannerId$.next(newBannerId);
                this.resetRotation();
            }
        });
        this.swipeListenersAdded = true;
    }
    async copyCode(el) {
        const width = getComputedStyle(el).width;
        el.style.width = width; // keep width consistent regardless of content
        const children = Array.from(el.children);
        children.forEach(c => c.classList.toggle("hidden"));
        const code = el.dataset.code;
        try {
            await navigator.clipboard.writeText(code);
        }
        catch (e) {
            console.error(e);
        }
        setTimeout(() => {
            children.forEach(c => c.classList.toggle("hidden"));
        }, 2000);
    }
    toggleDetails(el, close = false) {
        if (!close || (close && el.classList.contains("global-banner-promo__details-toggle--open"))) {
            this.debug("toggling details");
            el.classList.toggle("global-banner-promo__details-toggle--open");
            const detailsContainerEl = el.closest('.global-banner__banner').querySelector(".global-banner__details");
            if (detailsContainerEl) {
                if (detailsContainerEl.classList.contains("global-banner__details--open")) {
                    detailsContainerEl.style.top = '0';
                    if (this.focusTrap) {
                        this.focusTrap.deactivate();
                    }
                }
                else {
                    detailsContainerEl.style.top = `${this.offsetHeight}px`;
                    this.focusTrap = createFocusTrap(detailsContainerEl, {
                        checkCanFocusTrap: waitForTabbableElements,
                        allowOutsideClick: true
                    });
                    this.focusTrap.activate();
                }
                detailsContainerEl.classList.toggle("global-banner__details--open");
            }
        }
    }
    display(toHide) {
        if (toHide) {
            const spaceTaken = this.container.offsetHeight;
            this.container.classList.add('global-banner--hidden');
            if ((isMobile() && this.device === 'mobile') || (!isMobile() && this.device === 'desktop')) {
                updateBodyPaddingTop(spaceTaken);
            }
        }
        else {
            this.container.classList.remove('global-banner--hidden');
        }
    }
    debug(msg) {
        console.debug(`global-banner ${this.device}: ${msg}`);
    }
    /**
     * to determine if it's the banner itself (as opposed to the banner details)
     */
    isBanner(el) {
        return !!(el.classList.contains("js-global-banner") || el.closest(".js-global-banner"));
    }
}
if (!window.customElements.get("global-banner")) {
    customElements.define("global-banner", GlobalBanner);
}
